import {
  api,
  OnlineSpeakingMonitoringDto,
  OnlineSpeakingMonitoringsResponse,
  OnlineSpeakingMonitoringDetailsDto,
  CreateOnlineSpeakingMonitoringRequest,
  UpdateOnlineSpeakingMonitoringRequest,
  OnlineSpeakingMonitoringAvailableResultsForNewMonitoringResponse,
  OnlineSpeakingMonitoringAvailableResultsForExistingMonitoringResponse,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';

const onlineSpeakingMonitoringApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    onlineSpeakingMonitoringList: builder.query<
      OnlineSpeakingMonitoringDto[],
      { examinerId: number }
    >({
      query: ({ examinerId }) => api.onlineSpeakingMonitoring.get({ examinerId }),
      transformResponse: (response: OnlineSpeakingMonitoringsResponse) => response.monitorings,
      providesTags: ['OnlineSpeakingMonitoring'],
    }),
    onlineSpeakingMonitoring: builder.query<OnlineSpeakingMonitoringDetailsDto, { id: number }>({
      query: ({ id }) => api.onlineSpeakingMonitoring.getDetails({ id }),
      providesTags: ['OnlineSpeakingMonitoring'],
    }),
    createOnlineSpeakingMonitoring: builder.mutation<string, CreateOnlineSpeakingMonitoringRequest>(
      {
        query: (request) => api.onlineSpeakingMonitoring.create(request),
        invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
      }
    ),
    updateOnlineSpeakingMonitoring: builder.mutation<
      void,
      { id: number; request: UpdateOnlineSpeakingMonitoringRequest }
    >({
      query: ({ id, request }) => api.onlineSpeakingMonitoring.update({ id }, request),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    deleteOnlineSpeakingMonitoring: builder.mutation<void, { id: number }>({
      query: ({ id }) => api.onlineSpeakingMonitoring.delete({ id }),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    restoreDeletedOnlineSpeakingMonitoring: builder.mutation<void, { id: number }>({
      query: ({ id }) => api.onlineSpeakingMonitoring.restoreDeleted({ id }),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    restoreArchivedOnlineSpeakingMonitoring: builder.mutation<void, { id: number }>({
      query: ({ id }) => api.onlineSpeakingMonitoring.restoreArchived({ id }),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    updateOnlineSpeakingMonitoringFollowUp: builder.mutation<
      void,
      { id: number; hasFollowUp: boolean }
    >({
      query: ({ id, hasFollowUp }) =>
        api.onlineSpeakingMonitoring.updateFollowUp({ id }, { hasFollowUp }),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    bulkDeleteOnlineSpeakingMonitoring: builder.mutation<void, { ids: number[] }>({
      query: ({ ids }) => api.onlineSpeakingMonitoring.bulkDelete({ ids }),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    bulkArchiveOnlineSpeakingMonitoring: builder.mutation<void, { ids: number[] }>({
      query: ({ ids }) => api.onlineSpeakingMonitoring.archive({ ids }),
      invalidatesTags: ['OnlineSpeakingMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    onlineSpeakingMonitoringResultsForNew: builder.query<
      OnlineSpeakingMonitoringAvailableResultsForNewMonitoringResponse,
      { examinerId: number; monitoringDate: string }
    >({
      query: ({ examinerId, monitoringDate }) =>
        api.onlineSpeakingMonitoring.getAvailableOnlineSpeakingMonitoringResultsForNewMonitoring({
          examinerId,
          monitoringDate,
        }),
    }),
    onlineSpeakingMonitoringResultsForExisting: builder.query<
      OnlineSpeakingMonitoringAvailableResultsForExistingMonitoringResponse,
      { id: number; monitoringDate: string }
    >({
      query: ({ id, monitoringDate }) =>
        api.onlineSpeakingMonitoring.getAvailableOnlineSpeakingMonitoringResultsForExistingMonitoring(
          { id, monitoringDate }
        ),
    }),
  }),
});

export const {
  useOnlineSpeakingMonitoringListQuery,
  useOnlineSpeakingMonitoringQuery,
  useCreateOnlineSpeakingMonitoringMutation,
  useUpdateOnlineSpeakingMonitoringMutation,
  useDeleteOnlineSpeakingMonitoringMutation,
  useRestoreDeletedOnlineSpeakingMonitoringMutation,
  useRestoreArchivedOnlineSpeakingMonitoringMutation,
  useUpdateOnlineSpeakingMonitoringFollowUpMutation,
  useBulkDeleteOnlineSpeakingMonitoringMutation,
  useBulkArchiveOnlineSpeakingMonitoringMutation,
  useOnlineSpeakingMonitoringResultsForNewQuery,
  useOnlineSpeakingMonitoringResultsForExistingQuery,
} = onlineSpeakingMonitoringApi;
